import { Flex, ScrollAreaAutosize } from "@mantine/core";
import { BoxTitle, Container } from "./StyledComponents";
import styled, { keyframes } from "styled-components";
import { theme } from "../../theme";
import {
  ArrowsUpDownIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import SvgIcon from "../common/Icons/SvgIcon";
import { ReactComponent as Icon } from "../../assets/clockError.svg";
import { useFilterContext } from "../../context/DashboardFilterContext";
import { getLiveNotArrived } from "../../api/dashboardApi";
import { useEffect, useState } from "react";
import TimeCounter from "../common/TimeCounter";



function initials(name: string) {
  return (name ?? "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}


const MissingToday = ({ flex }: { flex: any }) => {
  const { filters } = useFilterContext();
  const [employees, setEmployees] = useState<any[]>([]);
  // Bugün kaç kişi bekleniyordu — boş listenin sebebini bu ayırıyor.
  const [expected, setExpected] = useState<number | null>(null);
  const [sort, setSort] = useState<"asc" | "desc">("asc");

  const toggleSortDirection = () =>
    setSort((prev) => (prev === "asc" ? "desc" : "asc"));

  const getNotArrived = async () => {
    try {
      const response: any = await getLiveNotArrived(filters, sort);
      if (response?.success) {
        setEmployees(response?.data?.employees || []);
        setExpected(response?.data?.expected ?? null);
      }
    } catch (error) {}
  };

  useEffect(() => {
    getNotArrived();
  }, [filters, sort]);

  return (
    <Container $flex={flex}>
      <HeaderRow>
        <Flex align="center" gap={10}>
          <HeaderIcon>
            <ExclamationCircleIcon width={18} />
          </HeaderIcon>
          <BoxTitle>Henüz Gelmeyenler</BoxTitle>
        </Flex>
        <SortBtn onClick={toggleSortDirection} $flipped={sort === "desc"}>
          <ArrowsUpDownIcon
            strokeWidth={2}
            color={theme.colors.brand.normal}
            height={18}
          />
        </SortBtn>
      </HeaderRow>

      <ScrollAreaAutosize h="90%" scrollbarSize={4}>
        {/*
          ⚠ Boş kutu iki ayrı şey demekti (plan 2.60): "vardiya planı yok,
          kimse beklenmiyor" ile "beklenen herkes geldi". İkisi çok farklı
          haber; kutu ikisinde de sessizdi.
        */}
        {employees?.length === 0 && (
          <BosDurum>
            {expected === 0
              ? "Bugün için vardiya planı yok — kimse beklenmiyor."
              : "Beklenen herkes gelmiş görünüyor."}
          </BosDurum>
        )}
        <EmpList>
          {employees?.map((employee: any, index: number) => {
            return (
              <div key={index}>
                <EmpRow $delay={index * 45}>
                  <GradAvatar>
                    {initials(employee?.name)}
                  </GradAvatar>

                  <EmpInfo>
                    <EmpName>{employee?.name}</EmpName>
                    <EmpMetaColumn>
                      <span className="dept">{employee?.department}</span>
                      <span className="role">{employee?.role}</span>
                    </EmpMetaColumn>
                  </EmpInfo>

                  <LateBadge>
                    <SvgIcon
                      color={theme.colors.alert.red}
                      Icon={Icon}
                      width={14}
                    />
                    <LateTime>
                      <TimeCounter initialTime={employee?.hoursLate} />
                    </LateTime>
                  </LateBadge>
                </EmpRow>
                {index !== employees?.length - 1 && <ListDivider />}
              </div>
            );
          })}
        </EmpList>
      </ScrollAreaAutosize>
    </Container>
  );
};

export { MissingToday };
export default MissingToday;


const BosDurum = styled.div`
  padding: 28px 12px;
  text-align: center;
  font-size: 13.5px;
  /* Sıkı satır yüksekliği Türkçe büyük harflerin tepesini kırpıyor. */
  line-height: 19px;
  color: #94a3b8;
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;


const AVATAR_COLORS = {
  from: "#d3d8f0", 
  to: "#afb6f5",   
};

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const HeaderIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${theme.colors.brand.normal}15;
  color: ${theme.colors.brand.normal};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid ${theme.colors.brand.normal}20;
`;

const SortBtn = styled.button<{ $flipped: boolean }>`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;
  transform: ${(p) => (p.$flipped ? "rotate(180deg)" : "rotate(0deg)")};
  &:hover { background: #e2e8f0; }
`;

const EmpList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 4px; /* Yanlardan milimetrik daralma */
`;

const EmpRow = styled.div<{ $delay: number }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 12px;
  margin: 0 2px;
  animation: ${slideUp} 0.3s ease both;
  animation-delay: ${(p) => p.$delay}ms;
  border-radius: 12px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); 
  cursor: pointer;
  position: relative;
  background: transparent;

  &:hover {
    transform: translateY(-2px) scale(1.015); /* Enlemesine taşma yapmayacak kadar büyüme */
    background: #ffffff;
    box-shadow: 
      0 10px 15px -3px rgba(0, 0, 0, 0.08),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
    z-index: 10; 
  }

  &:active {
    transform: translateY(-1px) scale(0.98);
  }
`;

const GradAvatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 30px;
  background: linear-gradient(135deg, ${AVATAR_COLORS.from}, ${AVATAR_COLORS.to});
  color: white;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
`;

const EmpInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const EmpName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmpMetaColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: 2px;

  .dept {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .role {
    font-size: 11px;
    color: #94a3b8;
    font-weight: 400;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const LateBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 50px;
  flex-shrink: 0;
`;

const LateTime = styled.div`
  font-size: 13.5px;
  font-weight: 700;
  color: ${theme.colors.alert.red};
  font-variant-numeric: tabular-nums;
  min-width: 50px;
`;

const ListDivider = styled.div`
  height: 1px;
  background: #f1f5f9;
  margin: 0 10px;
`;